// Calendar write-through orchestration (Phase 5). Firestore is the source of
// truth; the shared hub calendar is a projection. Callers fire these
// best-effort AFTER the Firestore save — a 'skipped' result or a thrown API
// error just leaves the row stale, which the sync chip surfaces with a retry.
import {
  hasConnectedBefore,
  buildEngagementEvent, buildTeamDateEvent,
  calendarUpsertEvent, calendarDeleteEvent,
} from './google.js'
import { stampCalendarSync } from './data.js'

function canSync(calendarId) {
  return Boolean(calendarId) && hasConnectedBefore()
}

/** Upsert the engagement's event and stamp the doc. */
export async function syncEngagementEvent(eng, calendarId, actorUid) {
  if (!canSync(calendarId)) return 'skipped'
  const body = buildEngagementEvent(eng)
  if (!body) {
    // Date cleared after a successful sync: take the projection down too,
    // or the event orphans invisibly (the chip renders nothing for 'na').
    if (eng.calendarEventId) {
      await calendarDeleteEvent(calendarId, eng.calendarEventId)
      await stampCalendarSync('engagements', eng.id, null, actorUid)
      return 'removed'
    }
    return 'skipped'
  }
  const eventId = await calendarUpsertEvent(calendarId, eng.calendarEventId || null, body)
  await stampCalendarSync('engagements', eng.id, eventId, actorUid)
  return 'synced'
}

/** Upsert the OOO row's event and stamp the doc. */
export async function syncTeamDateEvent(d, calendarId, actorUid) {
  if (!canSync(calendarId)) return 'skipped'
  const body = buildTeamDateEvent(d)
  if (!body) {
    // Date cleared after a successful sync: take the projection down too,
    // or the event orphans invisibly (the chip renders nothing for 'na').
    if (d.calendarEventId) {
      await calendarDeleteEvent(calendarId, d.calendarEventId)
      await stampCalendarSync('teamDates', d.id, null, actorUid)
      return 'removed'
    }
    return 'skipped'
  }
  const eventId = await calendarUpsertEvent(calendarId, d.calendarEventId || null, body)
  await stampCalendarSync('teamDates', d.id, eventId, actorUid)
  return 'synced'
}

/**
 * Best-effort event removal for a deleted row. The Firestore doc is already
 * (or about to be) gone, so failures only orphan a calendar event a human can
 * delete — log and tolerate.
 */
export async function removeCalendarEvent(docData, calendarId) {
  if (!canSync(calendarId) || !docData?.calendarEventId) return
  try {
    await calendarDeleteEvent(calendarId, docData.calendarEventId)
  } catch (e) {
    console.warn('Calendar event cleanup failed (orphaned event):', e.message)
  }
}
